import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WizardLayout } from '../../components/wizard';
import { useConfigurator } from '../../store/configurator';
import { ThemesSection } from '../design/components/ThemesSection';
import { ThemesCatalog } from '../design/components/ThemesCatalog';
import { Status, StatusText, ErrorText } from '../design/design.styles';

/**
 * v0 Step 1 — Theme. Pick a base theme; the live preview (the wizard's default main
 * pane) updates from the store selection. Colours + art style are their own step (step 2)
 * in this shorter flow, unlike v3's combined "Design" step.
 *
 * Reuses v3's `ThemesSection` + `ThemesCatalog` (copied under this flow's own folder, so
 * they read this flow's `useConfigurator` context).
 */
export const ThemeStep = (): React.ReactElement => {
  const { t } = useTranslation();
  const { status } = useConfigurator();
  const [catalogOpen, setCatalogOpen] = useState(false);

  return (
    <WizardLayout
      currentStep={0}
      title={t('Choose your theme')}
      description={t('Pick a base theme for your casino platform. You can fine-tune the colors and art style next.')}
      nextDisabled={status !== 'ready'}
      overlay={catalogOpen ? <ThemesCatalog onClose={() => setCatalogOpen(false)} /> : null}
    >
      {status === 'loading' ? (
        <Status>
          <StatusText text="Loading themes…" tag="span" />
        </Status>
      ) : null}
      {status === 'error' ? (
        <Status>
          <ErrorText text="Couldn’t load the configurator. Please try again." tag="span" />
        </Status>
      ) : null}
      {status === 'ready' ? <ThemesSection onBrowse={() => setCatalogOpen(true)} /> : null}
    </WizardLayout>
  );
};

export default ThemeStep;
